import { useState } from 'react';
import classNames from 'classnames';

import { Box } from 'ui/box';
import { Stack } from 'ui/stack';

import NavButton from 'containers/user/home/components/shared/navigation-button';

import { updateUserWidget } from 'api/generated/user-widgets/user-widgets';

import Icons from 'assets/icons';

export const announcementConfig = {
  colSpan: 4,
  rowSpan: {
    default: 3,
    sm: 4,
    xs: 4,
    tablet: 4,
    mobile: 5,
  },
};

type AnnouncementType = 'Warning' | 'Success' | 'Info' | 'Critical';

interface TypeStyle {
  bg: string;
  titleColor: string;
  border?: string;
}

const typeStyles: Record<AnnouncementType, TypeStyle> = {
  Warning: {
    bg: 'bg-amber-100',
    titleColor: 'text-amber-950',
  },
  Success: {
    bg: 'bg-success-100',
    titleColor: 'text-success-800',
  },
  Info: {
    bg: 'bg-navy-50',
    titleColor: 'text-navy-800',
    border: 'border border-navy-200',
  },
  Critical: {
    bg: 'bg-warning-100',
    titleColor: 'text-critical-950',
  },
};

const SkeletonLoader = (): React.JSX.Element => (
  <div
    aria-label="Loading content"
    role="status"
    className="h-full w-full p-5 lg:py-3 bg-gray-100 rounded-md animate-pulse flex flex-col gap-y-3 tablet:gap-y-0 justify-between tablet:flex-row"
  >
    <span className="sr-only">Loading</span>
    <Stack sx={{ flex: '1 1 0%', rowGap: 1 }}>
      <Stack direction="row">
        <Box
          sx={{
            height: '1.5rem',
            width: '25%',
            bgcolor: 'tokens.gray.300',
            borderRadius: '0.25rem',
          }}
        />
      </Stack>
      <Box
        sx={{
          height: '1rem',
          width: '75%',
          bgcolor: 'tokens.gray.300',
          borderRadius: '0.25rem',
        }}
      />
      <Box
        sx={{
          height: '1rem',
          width: '66.666667%',
          bgcolor: 'tokens.gray.300',
          borderRadius: '0.25rem',
        }}
      />
    </Stack>
    <Box
      sx={{
        height: '2rem',
        width: '8rem',
        bgcolor: 'tokens.gray.300',
        borderRadius: '0.25rem',
        mt: 1.5,
        '@media (min-width: 780px)': { mt: 0, ml: 'auto' },
      }}
    />
  </div>
);

interface AnnouncementAttributes {
  title?: string;
  description?: string;
  targetLink?: string;
  type?: AnnouncementType;
}

interface AnnouncementData {
  attributes?: AnnouncementAttributes;
}

interface AnnouncementProps {
  data?: AnnouncementData;
  loading?: boolean;
  userId: string;
  widgetId: string | number;
  removeWidget: (widgetId: string | number) => void;
}

const Announcement = ({
  data,
  loading,
  userId,
  widgetId,
  removeWidget,
}: AnnouncementProps): React.JSX.Element => {
  const [isClosing, setIsClosing] = useState(false);

  if (loading) {
    return <SkeletonLoader />;
  }

  const {
    title,
    description,
    targetLink,
    type = 'Info',
  } = data?.attributes || {};

  const { bg, titleColor, border } = typeStyles[type];

  const handleClose = async (): Promise<void> => {
    if (isClosing) return;

    setIsClosing(true);

    try {
      await updateUserWidget(userId, Number(widgetId), { viewed: true });

      removeWidget(widgetId);
    } catch (error) {
      console.error('Error updating widget:', error);
      setIsClosing(false);
    }
  };

  return (
    <div
      className={classNames(
        'h-full w-full px-5 py-3 rounded-md flex flex-col tablet:flex-row tablet:items-center justify-between relative transition-all duration-300 ease-in-out',
        bg,
        border,
        {
          'opacity-0 scale-95': isClosing,
          'opacity-100 scale-100': !isClosing,
        }
      )}
    >
      <button
        onClick={handleClose}
        className="absolute top-3 right-3 w-6 h-6 flex items-center justify-center hover:bg-black/10 rounded transition-colors"
        aria-label="Close announcement"
      >
        <Icons.Close className="w-4 h-4 text-gray-600" aria-hidden="true" />
      </button>

      <Stack
        sx={{
          height: '100%',
          pr: 4,
          justifyContent: targetLink ? 'space-between' : 'center',
        }}
      >
        <Box>
          <Stack
            direction="row"
            sx={{ mb: 1, '@media (min-width: 780px)': { mb: 0 } }}
          >
            <p className={classNames('font-bold text-xl', titleColor)}>
              {title}
            </p>
          </Stack>
          <p className="text-neutral-950 text-base font-normal">
            {description}
          </p>
        </Box>
        {targetLink && <NavButton link={targetLink} text="Read more" />}
      </Stack>
    </div>
  );
};

export default Announcement;
